import { portalOf } from '../contexts/MarketContext';
import { totalTravellers } from '../utils/groupSize';

// ── Season goals ─────────────────────────────────────────────────────────────
// Targets started life in the "26/27 Trip Type Guide": how many tours and
// travellers are needed each month to cover the £12k monthly overhead, based on
// 25/26 tour averages. A tour counts towards its target once its deposit is
// marked paid, and lands in the month it DEPARTS (not the month the deposit
// arrived), because the overhead being covered is the overhead of the month the
// tour runs.
//
// The season and its goals are admin-editable: they live in Firestore at
// `goalSettings/current` (see useGoalConfig) and the values below are only the
// fallback used until an admin first saves. Every goal belongs to one portal
// and claims a list of group types, plus any group type spelled the same as
// the goal's own name (a "Long Haul" goal always takes "Long Haul" tours). A
// goal with no listed group types is also that portal's catch-all.

export const GOAL_SETTINGS_PATH = ['goalSettings', 'current'];

export const DEFAULT_SEASON = {
  label: '26/27',
  startMonth: '2026-10',
  endMonth: '2027-07',
  monthlyOverhead: 12000,
};

// `pace: 'monthly'` targets are quoted per month, so a fair read of progress
// scales them by how many season months have elapsed, and the season totals
// are derived from them. `pace: 'season'` targets are whole-season totals —
// LLS and Long Haul run a handful of tours whenever they land, so pacing them
// monthly would show them permanently "behind".
export const DEFAULT_GOALS = [
  {
    key: 'your_tour',
    label: 'Your Tour',
    color: '#1A4A8A',
    portal: 'uk_to_spain',
    groupTypes: [], // catch-all: every UK → Spain tour not claimed below
    pace: 'monthly',
    toursPerMonth: 2.5,
    travellersPerMonth: 100,
  },
  {
    key: 'lls',
    label: 'LLS',
    color: '#1A7A4A',
    portal: 'uk_to_spain',
    groupTypes: ['LLS'],
    pace: 'season',
    tours: 5,
    travellers: 250,
  },
  {
    key: 'long_haul',
    label: 'Long Haul',
    color: '#7C3AED',
    portal: 'uk_to_spain',
    groupTypes: ['Long Haul', 'NSG', 'MIC Cup'],
    pace: 'season',
    tours: 2,
    travellers: 200,
  },
  {
    key: 'spain_to_uk',
    label: 'Spain to UK',
    color: '#E8A020',
    portal: 'spain_to_uk',
    groupTypes: [],
    pace: 'monthly',
    toursPerMonth: 1,
    travellersPerMonth: 30,
  },
  {
    key: 'padelante',
    label: 'Padelante',
    color: '#8DC63F', // the Padelante accent, as used on their quote letter PDF
    portal: 'padelante',
    groupTypes: [],
    pace: 'season',
    tours: 12,
    travellers: 120,
  },
];

/** 'YYYY-MM' keys from start to end inclusive. Empty if the range is invalid. */
export function seasonMonthsOf(season) {
  const out = [];
  const start = season && season.startMonth;
  const end = season && season.endMonth;
  if (!/^\d{4}-\d{2}$/.test(start || '') || !/^\d{4}-\d{2}$/.test(end || '') || start > end) return out;
  const [sy, sm] = start.split('-').map(Number);
  const [ey, em] = end.split('-').map(Number);
  let y = sy;
  let m = sm;
  while ((y < ey || (y === ey && m <= em)) && out.length < 36) {
    out.push(`${y}-${String(m).padStart(2, '0')}`);
    m += 1;
    if (m > 12) { m = 1; y += 1; }
  }
  return out;
}

const num = (v, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 ? n : fallback;
};

/** Group types compare case/space-insensitively ("Mic Cup" = "MIC Cup"). */
export function normGroupType(s) {
  return String(s || '').trim().replace(/\s+/g, ' ').toUpperCase();
}

/**
 * Fill in a goal's derived figures. Monthly goals get season totals from their
 * per-month rate; season goals get a travellers-per-tour average for display.
 */
export function normalizeGoal(g, seasonLength) {
  const pace = g.pace === 'monthly' ? 'monthly' : 'season';
  const base = {
    key: g.key,
    label: g.label || 'Untitled goal',
    color: g.color || '#1A4A8A',
    portal: g.portal || 'uk_to_spain',
    groupTypes: (g.groupTypes || []).map((s) => String(s).trim()).filter(Boolean),
    pace,
  };
  if (pace === 'monthly') {
    const toursPerMonth = num(g.toursPerMonth);
    const travellersPerMonth = num(g.travellersPerMonth);
    return {
      ...base,
      toursPerMonth,
      travellersPerMonth,
      tours: Math.round(toursPerMonth * seasonLength * 10) / 10,
      travellers: Math.round(travellersPerMonth * seasonLength),
    };
  }
  const tours = num(g.tours);
  const travellers = num(g.travellers);
  return {
    ...base,
    tours,
    travellers,
    travellersPerTour: tours > 0 ? Math.round(travellers / tours) : 0,
  };
}

/** Turn a stored (or missing) settings doc into a ready-to-use config. */
export function normalizeGoalConfig(raw) {
  const season = { ...DEFAULT_SEASON, ...((raw && raw.season) || {}) };
  season.monthlyOverhead = num(season.monthlyOverhead, DEFAULT_SEASON.monthlyOverhead);
  const months = seasonMonthsOf(season);
  const goals = ((raw && Array.isArray(raw.goals)) ? raw.goals : DEFAULT_GOALS)
    .filter((g) => g && g.key)
    .map((g) => normalizeGoal(g, months.length));
  return { season, months, goals };
}

export const DEFAULT_CONFIG = normalizeGoalConfig(null);

// Back-compat exports for the default season.
export const SEASON = DEFAULT_CONFIG.season;
export const SEASON_MONTHS = DEFAULT_CONFIG.months;
export const SEASON_LENGTH = SEASON_MONTHS.length;
export const GOAL_CATEGORIES = DEFAULT_CONFIG.goals;
export const GOAL_BY_KEY = Object.fromEntries(GOAL_CATEGORIES.map((g) => [g.key, g]));

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** '2026-10' → 'Oct 26' */
export function monthLabel(key) {
  if (!key) return '';
  const [y, m] = key.split('-');
  return `${MONTH_SHORT[Number(m) - 1]} ${y.slice(2)}`;
}

/** '£12k' style short money label for the overhead line. */
export function overheadLabel(amount) {
  const n = num(amount);
  return n >= 1000 && n % 1000 === 0 ? `£${n / 1000}k` : `£${n.toLocaleString('en-GB')}`;
}

/** Every group type a goal claims: its listed ones plus its own name. */
export function claimedGroupTypes(goal) {
  const out = new Set((goal.groupTypes || []).map(normGroupType));
  const own = normGroupType(goal.label);
  if (own) out.add(own);
  return out;
}

/**
 * Which goal a tour counts towards, or null if it counts towards none.
 *
 * Portal wins over group type: a Padelante tour only ever counts towards a
 * Padelante goal, a Spain → UK tour towards a Spain → UK goal. Within its
 * portal a tour goes to the first goal that claims its group type (listed, or
 * the goal's own name), otherwise to the portal's catch-all goal (one with no
 * listed group types), if there is one.
 */
export function goalCategoryOf(r, goals = GOAL_CATEGORIES) {
  if (!r) return null;
  const portal = portalOf(r);
  const gt = normGroupType(r.groupType);
  const inPortal = goals.filter((g) => g.portal === portal);
  if (gt) {
    const named = inPortal.find((g) => claimedGroupTypes(g).has(gt));
    if (named) return named.key;
  }
  const catchAll = inPortal.find((g) => g.groupTypes.length === 0);
  return catchAll ? catchAll.key : null;
}

// Statuses at or beyond deposit paid. `stageProgress.deposit_paid` is the
// primary signal — it persists as the tour advances, whereas `status` moves on
// to pre_tour/tour_live/completed — but the status list catches older tours
// that were moved along before the stage checklist existed.
const DEPOSIT_PAID_STATUSES = new Set([
  'deposit_paid', 'pre_tour', 'tour_live', 'completed', 'confirmed',
]);

/** True once a tour's deposit is paid, so it counts towards the targets. */
export function isDepositPaid(r) {
  if (!r || r.status === 'cancelled') return false;
  if (r.stageProgress && r.stageProgress.deposit_paid) return true;
  return DEPOSIT_PAID_STATUSES.has(r.status);
}

/** Travellers on a tour — the whole travelling group: players, supporters and the customer's staff. */
export function travellersOf(r) {
  return totalTravellers(r);
}

/** The season month a tour lands in, or null if it departs outside the season. */
export function seasonMonthOf(r, months = SEASON_MONTHS) {
  const d = r && r.departureDate;
  if (!d || d.length < 7) return null;
  const key = d.slice(0, 7);
  return months.includes(key) ? key : null;
}

/** How many season months have elapsed (inclusive of the current one). */
export function monthsElapsed(today = new Date(), months = SEASON_MONTHS) {
  if (!months.length) return 0;
  const key = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
  if (key < months[0]) return 0;
  if (key > months[months.length - 1]) return months.length;
  return months.indexOf(key) + 1;
}

/** The goals visible to a viewer, given the portals they can access. */
export function goalsForPortals(portals, goals = GOAL_CATEGORIES) {
  if (!portals) return goals;
  return goals.filter((g) => portals.includes(g.portal));
}

/**
 * Where a category should be by now. Monthly-paced goals scale with the season;
 * season-total goals have no meaningful mid-season expectation, so they return
 * null and the UI omits the pace read rather than inventing one.
 */
export function expectedToDate(goal, elapsed) {
  if (goal.pace !== 'monthly') return null;
  return {
    tours: goal.toursPerMonth * elapsed,
    travellers: goal.travellersPerMonth * elapsed,
  };
}

/**
 * Tally deposit-paid tours into each goal, by month and in total.
 *
 * `toDate` counts only the months that have already elapsed. That's the figure
 * the pace read has to use: because tours are bucketed by DEPARTURE month, the
 * season-wide `tours` total includes trips that haven't happened yet, and
 * comparing it against "what should have departed by now" would flatter the
 * pace — three tours spread across Oct, Feb and Apr are not three tours' worth
 * of October.
 */
export function aggregateGoals(requests, goals, elapsed, months = SEASON_MONTHS) {
  const el = elapsed == null ? monthsElapsed(new Date(), months) : elapsed;
  const elapsedMonths = new Set(months.slice(0, el));
  const out = {};
  for (const g of goals) {
    out[g.key] = {
      tours: 0,
      travellers: 0,
      toDate: { tours: 0, travellers: 0 },
      months: Object.fromEntries(months.map((m) => [m, { tours: 0, travellers: 0 }])),
      list: [],
    };
  }
  for (const r of requests || []) {
    const key = goalCategoryOf(r, goals);
    if (!key || !out[key]) continue;
    if (!isDepositPaid(r)) continue;
    const month = seasonMonthOf(r, months);
    if (!month) continue;
    const pax = travellersOf(r);
    const bucket = out[key];
    bucket.tours += 1;
    bucket.travellers += pax;
    bucket.months[month].tours += 1;
    bucket.months[month].travellers += pax;
    bucket.list.push(r);
    if (elapsedMonths.has(month)) {
      bucket.toDate.tours += 1;
      bucket.toDate.travellers += pax;
    }
  }
  return out;
}

/** Season totals across a set of goals, for the summary bar. */
export function goalTotals(goals, data) {
  let tTarget = 0, tDone = 0, pTarget = 0, pDone = 0;
  for (const g of goals) {
    tTarget += g.tours;
    pTarget += g.travellers;
    tDone += data[g.key]?.tours || 0;
    pDone += data[g.key]?.travellers || 0;
  }
  return { tTarget: Math.round(tTarget * 10) / 10, tDone, pTarget, pDone };
}

/**
 * Problems that would make a goal set ambiguous, as human-readable strings.
 * Two goals in one portal claiming the same group type, or two catch-alls in
 * one portal, would make where a tour counts depend on list order.
 */
export function validateGoals(goals) {
  const problems = [];
  const seenKeys = new Set();
  const byPortal = {};
  for (const g of goals) {
    if (!String(g.label || '').trim()) problems.push('Every goal needs a name.');
    if (seenKeys.has(g.key)) problems.push(`Duplicate goal key "${g.key}".`);
    seenKeys.add(g.key);
    const p = (byPortal[g.portal] = byPortal[g.portal] || { catchAll: [], types: {} });
    if (!g.groupTypes || g.groupTypes.length === 0) p.catchAll.push(g.label);
    for (const k of claimedGroupTypes(g)) {
      if (p.types[k] && p.types[k] !== g.label) {
        problems.push(`Group type "${k}" is claimed by both ${p.types[k]} and ${g.label} (a goal's name counts as a group type).`);
      } else p.types[k] = g.label;
    }
  }
  for (const p of Object.values(byPortal)) {
    if (p.catchAll.length > 1) {
      problems.push(`${p.catchAll.join(' and ')} both have no group types — only one goal per portal can catch "everything else".`);
    }
  }
  return [...new Set(problems)];
}
